import { Component } from '@theme/component';

/** A reusable, keyboard-accessible listbox for product option values. */
class OptionSelectorComponent extends Component {
  requiredRefs = ['trigger', 'list', 'value'];

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener('pointerdown', this.#onOutsidePointerDown);
  }

  disconnectedCallback() {
    document.removeEventListener('pointerdown', this.#onOutsidePointerDown);
    super.disconnectedCallback();
  }

  get value() {
    return this.dataset.value || '';
  }

  get isOpen() {
    return this.refs.trigger.getAttribute('aria-expanded') === 'true';
  }

  toggle() {
    if (this.isOpen) {
      this.close();
    } else {
      this.open();
    }
  }

  open() {
    if (this.isOpen) return;
    this.refs.list.hidden = false;
    this.refs.trigger.setAttribute('aria-expanded', 'true');

    const selected = this.querySelector('.option-selector__option[aria-selected="true"]');
    this.#focusOption(selected || this.options[0]);
  }

  close({ restoreFocus = false } = {}) {
    if (!this.isOpen) return;
    this.refs.trigger.setAttribute('aria-expanded', 'false');
    this.refs.list.hidden = true;
    if (restoreFocus) this.refs.trigger.focus();
  }

  selectOption(event) {
    const option = event.target.closest('.option-selector__option');
    if (!option) return;

    if (this.hasAttribute('data-server-rendered')) {
      this.close({ restoreFocus: true });
      this.dispatchEvent(new CustomEvent('change', {
        bubbles: true,
        detail: { variantId: option.dataset.variantId },
      }));
      return;
    }

    this.dataset.value = option.dataset.value;
    this.refs.value.textContent = option.textContent.trim();
    for (const item of this.options) {
      item.setAttribute('aria-selected', String(item === option));
    }

    this.close({ restoreFocus: true });
    this.dispatchEvent(new Event('change', { bubbles: true }));
  }

  handleKeydown(event) {
    const key = event.key;

    if (key === 'Escape' && this.isOpen) {
      event.preventDefault();
      this.close({ restoreFocus: true });
      return;
    }

    if (key === 'Tab' && this.isOpen) {
      this.close();
      return;
    }

    if (!['ArrowDown', 'ArrowUp', 'Home', 'End', 'Enter', ' '].includes(key)) return;
    event.preventDefault();

    if (!this.isOpen) {
      this.open();
      return;
    }

    const options = this.options;
    const target = event.composedPath()[0];
    const current = options.indexOf(target);

    if (key === 'ArrowDown') this.#focusOption(options[Math.min(current + 1, options.length - 1)]);
    if (key === 'ArrowUp') this.#focusOption(options[Math.max(current - 1, 0)]);
    if (key === 'Home') this.#focusOption(options[0]);
    if (key === 'End') this.#focusOption(options.at(-1));
    if (key === 'Enter' || key === ' ') {
      const option = options[current];
      if (option) this.selectOption({ target: option });
    }
  }

  get options() {
    return [...this.querySelectorAll('.option-selector__option:not([aria-disabled="true"])')];
  }

  #focusOption(option) {
    if (!option) return;
    option.focus({ preventScroll: true });
    option.scrollIntoView({ block: 'nearest' });
  }

  #onOutsidePointerDown = (event) => {
    if (!this.contains(event.target)) this.close();
  };
}

if (!customElements.get('option-selector-component')) {
  customElements.define('option-selector-component', OptionSelectorComponent);
}
